import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  CONSENT_TEXT,
  createLearner,
  fetchLearners,
  removeLearner,
  renameLearner,
  type Learner,
} from '../learners/learners';
import { translate, useT } from '../i18n/i18n';

type Status = {
  status: string;
  plan: string;
  maxLearners: number;
};

export default function LearnersSection() {
  const t = useT();
  const [learners, setLearners] = useState<Learner[]>([]);
  const [maxLearners, setMaxLearners] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [consent, setConsent] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Learner | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, status] = await Promise.all([
        fetchLearners(),
        api<Status>('/subscription/status', { token: true }),
      ]);
      setLearners(list);
      setMaxLearners(status.maxLearners);
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Could not load learners.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function add() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createLearner(name);
      setLearners((cur) => [...cur, created]);
      setNewName('');
      setConsent(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not add that learner.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveName(id: string) {
    const name = editName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await renameLearner(id, name);
      setLearners((cur) => cur.map((l) => (l.id === id ? updated : l)));
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not rename that learner.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    setError(null);
    try {
      await removeLearner(pendingDelete.id);
      setLearners((cur) => cur.filter((l) => l.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not remove that learner.'));
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  const atCap = maxLearners !== null && learners.length >= maxLearners;

  return (
    <div className="card">
      <h2>{t('Learners')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t('Each learner keeps their own record of what they have been shown and how they answered. Programs are shared across everyone on this account.')}
      </p>

      {error && <div className="error" role="alert">{error}</div>}

      {loading ? (
        <p className="muted">{t('Loading…')}</p>
      ) : (
        <>
          <ul className="list">
            {learners.map((l) => (
              <li className="list-item" key={l.id}>
                {editing === l.id ? (
                  <>
                    <div className="list-item-text">
                      <input
                        aria-label={t('Rename {name}', { name: l.name })}
                        value={editName}
                        maxLength={80}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    </div>
                    <div className="toolbar">
                      <button className="btn" onClick={() => saveName(l.id)} disabled={busy}>
                        {t('Save')}
                      </button>
                      <button className="btn ghost" onClick={() => setEditing(null)} disabled={busy}>
                        {t('Cancel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="list-item-text">
                      <div className="name">{l.name}</div>
                    </div>
                    <div className="toolbar">
                      <button
                        className="btn secondary"
                        onClick={() => { setEditing(l.id); setEditName(l.name); }}
                        disabled={busy}
                        aria-label={t('Rename {name}', { name: l.name })}
                      >
                        {t('Rename')}
                      </button>
                      <button
                        className="btn quiet-danger"
                        onClick={() => setPendingDelete(l)}
                        disabled={busy || learners.length <= 1}
                        aria-label={t('Remove {name}', { name: l.name })}
                      >
                        {t('Remove')}
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          <div className="row">
            <div className="field">
              <label htmlFor="newLearner">{t('Add a learner')}</label>
              <input
                id="newLearner"
                value={newName}
                maxLength={80}
                placeholder={t('Their name')}
                disabled={atCap || busy}
                onChange={(e) => setNewName(e.target.value)}
              />
              {maxLearners !== null && (
                <span className="field-hint">
                  {maxLearners === 1
                    ? t('{used} of {max} used — this plan covers 1 learner.', { used: learners.length, max: maxLearners })
                    : t('{used} of {max} used — this plan covers up to {max} learners.', { used: learners.length, max: maxLearners })}
                  {atCap && ' ' + t('Move to a larger plan to add another.')}
                </span>
              )}
            </div>
          </div>

          <div className="field">
            <label className="check">
              <input
                type="checkbox"
                checked={consent}
                disabled={atCap || busy}
                onChange={(e) => setConsent(e.target.checked)}
              />
              {t(CONSENT_TEXT)}
            </label>
          </div>

          <div className="form-actions">
            <button className="btn" onClick={add} disabled={atCap || busy || !newName.trim() || !consent}>
              {busy ? t('Working…') : t('Add learner')}
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('Remove learner?')}
        message={
          pendingDelete
            ? t('"{name}" and everything recorded about their practice will be removed. This cannot be undone.', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={busy ? t('Removing…') : t('Remove')}
        cancelLabel={t('Keep')}
        destructive
        onConfirm={confirmDelete}
        onCancel={() => { if (!busy) setPendingDelete(null); }}
      />
    </div>
  );
}
